<!--
  @component Implement iframe for custom preview styles.
  @see https://decapcms.org/docs/customization/
  @see https://sveltiacms.app/en/docs/api/preview-styles
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { mount } from 'svelte';

  import Placeholder from '$lib/components/common/placeholder.svelte';
  import { escapeAttr } from '$lib/services/utils/string';

  /**
   * @import { Snippet } from 'svelte';
   * @import { InternalLocaleCode } from '$lib/types/private';
   */

  /**
   * @typedef {object} Props
   * @property {InternalLocaleCode} locale Current pane’s locale.
   * @property {string[]} styleURLs Custom stylesheet URLs to apply in the iframe.
   * @property {Snippet} children Preview content to render inside the iframe.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    locale,
    styleURLs,
    children,
    /* eslint-enable prefer-const */
  } = $props();

  /**
   * Reference to the iframe element used for custom preview.
   * @type {HTMLIFrameElement | undefined}
   */
  let iframe = $state();

  /**
   * Generate the HTML content for the iframe.
   * @returns {string} The HTML string.
   */
  const generateHTML = () => `
    <!DOCTYPE html>
    <html lang="${escapeAttr(locale)}">
    <head>
      <meta charset="UTF-8">
      ${styleURLs.map((url) => `<link rel="stylesheet" href="${escapeAttr(url)}">`).join('\n')}
    </head>
    <body></body>
    </html>
  `;

  /**
   * Mount the Svelte placeholder component into the iframe’s body. This way, the iframe content
   * becomes reactive and can be updated when the fields change.
   */
  const mountPlaceholder = () => {
    const target = iframe?.contentDocument?.body;

    if (target) {
      mount(Placeholder, { target, props: { children } });
    }
  };

  /**
   * Initialize the iframe with a custom stylesheet.
   */
  const initializeIframe = () => {
    if (iframe) {
      const blobURL = URL.createObjectURL(new Blob([generateHTML()], { type: 'text/html' }));

      iframe.addEventListener(
        'load',
        () => {
          mountPlaceholder();
          // The iframe has loaded the HTML document, so the blob URL is no longer needed
          URL.revokeObjectURL(blobURL);
        },
        { once: true },
      );

      iframe.src = blobURL;
    }
  };

  $effect(() => {
    if (iframe) {
      initializeIframe();
    }
  });
</script>

<iframe
  class="preview"
  title={_('content_preview')}
  sandbox="allow-same-origin allow-scripts"
  bind:this={iframe}
></iframe>

<style lang="scss">
  iframe {
    display: block;
    border: none;
    width: 100%;
    height: 100%;
  }
</style>
